import { useContext } from 'react'

import {
  Button, Form, Grid, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useCreateOrEditPublicationBookingModal from './hooks/useCreateOrEditPublicationBookingModal'

import LayoutTemplateTile from './components/LayoutTemplateTile'

const MODAL_KEY = 'CreateOrEditPublicationBookingModal'

type ModalCallbacks = {
  closeModal: () => void,
  createPublicationBooking: (payload: HandleCallbackActionParams) => void,
  updatePublicationBooking: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditPublicationBookingModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createPublicationBooking, updatePublicationBooking } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const publicationBooking = digObject(modalPayload, 'publicationBooking', {})
  const { createPublicationBooking: customCreateFn } = digObject(modalPayload, 'callbacks', {})

  const newRecord = !publicationBooking.id

  const createOrUpdatePublicationBookingModalPayload = useCreateOrEditPublicationBookingModal(publicationBooking)
  const {
    layoutTemplatesPayload: {
      filteredLayoutTemplates,
    },
    publicationBookingPayload: {
      callbacks: {
        createPublicationBooking: defaultCreateFn,
        updatePublicationBooking: updateFn,
      },
      creating,
      updating,
    },
    publicationBookingFormPayload: {
      entityState,
      entityState: {
        layout_template_id,
        publication_product_id,
      },
      saveEnabled,
      setEntityState,
    },
    sortedPublicationProducts,
  } = createOrUpdatePublicationBookingModalPayload

  const createFn = customCreateFn || defaultCreateFn

  const createOrUpdatePublicationBookingPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Publication Booking ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Publication Page"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Page Size">
            <select
              name="publication_product_id"
              onChange={e => setEntityState({
                layout_template_id: undefined,
                publication_product_id: e.target.value,
              })}
              value={publication_product_id}
            >
              <option value="">Please Select...</option>
              {sortedPublicationProducts.map(publicationProduct => (
                <option key={publicationProduct.id} value={publicationProduct.id}>
                  {publicationProduct.title}
                </option>
              ))}
            </select>
          </Form.Field>

          {!!publication_product_id && (
            <Form.Field label="Layout Template" marginTop="large">
              <Grid
                // @ts-expect-error TS2322
                gridGap="medium"
                gridAutoRows="1fr"
                gridTemplateColumns="repeat(auto-fill, minmax(160px, 1fr))"
                gridTemplateRows="repeat(auto-fill, minmax(160px, 1fr))"
              >
                {filteredLayoutTemplates.map(layoutTemplate => (
                  <LayoutTemplateTile
                    callbacks={{
                      selectLayoutTemplate: () => setEntityState({ layout_template_id: layoutTemplate.id }),
                    }}
                    key={layoutTemplate.id}
                    layoutTemplate={layoutTemplate}
                    selected={layoutTemplate.id === layout_template_id}
                  />
                ))}
              </Grid>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createPublicationBooking(createOrUpdatePublicationBookingPayload)
              : () => updatePublicationBooking(createOrUpdatePublicationBookingPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Publication Booking` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditPublicationBookingModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
